'use client'

import * as React from 'react'
import { Check, CircleAlert } from 'lucide-react'

import { Crest } from '@/components/encuentro/ui/crest'
import { cn } from '@/lib/utils'

/** The native select in the system's field look: the phone keeps its own picker. */
export const SELECT_CLASSES =
  'min-h-en-control w-full min-w-0 cursor-pointer rounded-en-md border border-en-line-control bg-en-surface px-4 py-2 font-en-sans text-en-body text-en-ink outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid focus-visible:outline-en-focus aria-invalid:border-en-danger aria-invalid:shadow-[0_0_0_1px_var(--en-danger)] disabled:cursor-not-allowed disabled:opacity-45'

/** What to fix, under a control; linked to it through `aria-describedby`. */
export function FieldError({ id, message }: { id?: string; message?: string | null }) {
  if (!message) return null
  return (
    <p id={id} className="flex items-start gap-2 text-en-meta text-en-danger">
      <CircleAlert aria-hidden className="mt-px size-4 shrink-0" />
      <span>{message}</span>
    </p>
  )
}

/** A select with its visible label, help and error, like `Field`. */
export function SelectField({
  label,
  value,
  none,
  noneDisabled,
  options,
  onChange,
  help,
  error,
  className,
  id: idProp,
}: {
  label: string
  value: string
  /** The empty option ("Sin industria"). */
  none: string
  /** A required select still shows the empty option but cannot go back to it. */
  noneDisabled?: boolean
  options: ReadonlyArray<{ value: string; label: string }>
  onChange: (value: string) => void
  help?: string
  error?: string | null
  className?: string
  id?: string
}) {
  const autoId = React.useId()
  const id = idProp ?? autoId
  const helpId = help ? `${id}-help` : undefined
  const errorId = error ? `${id}-error` : undefined
  return (
    <div className={cn('grid min-w-0 content-start gap-2 text-en-ink', className)}>
      <label htmlFor={id} className="text-en-label">
        {label}
      </label>
      {help && (
        <p id={helpId} className="text-en-meta text-en-ink-muted">
          {help}
        </p>
      )}
      <select
        id={id}
        value={value}
        aria-invalid={error ? true : undefined}
        aria-describedby={[helpId, errorId].filter(Boolean).join(' ') || undefined}
        onChange={(event) => onChange(event.target.value)}
        className={SELECT_CLASSES}
      >
        <option value="" disabled={noneDisabled}>
          {none}
        </option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <FieldError id={errorId} message={error} />
    </div>
  )
}

/**
 * A labelled group around a control that brings its own markup (the city
 * search, the phone, a set of chips). The label names the group.
 */
export function FieldGroup({
  label,
  help,
  error,
  children,
  className,
  labelId,
}: {
  label: string
  help?: string
  error?: string | null
  children: React.ReactNode
  className?: string
  /** The label's id, for a control inside that names itself with it. */
  labelId?: string
}) {
  const id = React.useId()
  const ownLabelId = labelId ?? `${id}-label`
  return (
    <div
      role="group"
      aria-labelledby={ownLabelId}
      aria-describedby={
        [help ? `${id}-help` : '', error ? `${id}-error` : ''].filter(Boolean).join(' ') ||
        undefined
      }
      className={cn('grid min-w-0 content-start gap-2 text-en-ink', className)}
    >
      <span id={ownLabelId} className="text-en-label">
        {label}
      </span>
      {help && (
        <p id={`${id}-help`} className="text-en-meta text-en-ink-muted">
          {help}
        </p>
      )}
      {children}
      <FieldError id={`${id}-error`} message={error} />
    </div>
  )
}

/** A checkbox row with a 44 px target. */
export function CheckboxField({
  id: idProp,
  label,
  checked,
  onChange,
}: {
  /** For a caller that moves the focus here. */
  id?: string
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  const autoId = React.useId()
  const id = idProp ?? autoId
  return (
    <div className="flex min-h-en-control items-center gap-3">
      <span className="relative grid size-6 shrink-0 place-items-center">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          className="peer size-6 cursor-pointer appearance-none rounded-en-sm border border-en-line-control bg-en-surface checked:border-en-ink checked:bg-en-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid focus-visible:outline-en-focus"
        />
        <Check
          aria-hidden
          className="pointer-events-none absolute size-4 text-en-bg opacity-0 peer-checked:opacity-100"
        />
      </span>
      <label htmlFor={id} className="cursor-pointer text-en-body">
        {label}
      </label>
    </div>
  )
}

/** A toggle pill for multi-select options, like the classic chips but in the system. */
export function ToggleChip({
  label,
  pressed,
  onToggle,
  imageUrl,
}: {
  label: string
  pressed: boolean
  onToggle: () => void
  /** The school's crest for this option: it leads the label (the name stays). */
  imageUrl?: string
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onToggle}
      className={cn(
        'inline-flex min-h-10 max-w-full items-center gap-2 rounded-en-full border px-4 py-1 text-en-label [overflow-wrap:anywhere]',
        imageUrl && 'py-0 pl-1',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid focus-visible:outline-en-focus',
        pressed
          ? 'border-en-ink bg-en-ink text-en-bg'
          : 'border-en-line-control bg-en-surface text-en-ink hover:bg-en-surface-sunken'
      )}
    >
      {imageUrl && <Crest src={imageUrl} size="picker" />}
      {pressed && <Check aria-hidden className="size-4 shrink-0" />}
      {label}
    </button>
  )
}
